import type { ReactElement, ReactNode } from 'react'

import {
  LiveRegion,
  PillButton,
  SectionHeading,
} from '@maria-cavalheiro-website/shared-ui-web'

import { poemCardView } from './poem-card-view.ts'
import { PoemCard } from './poem-card.tsx'
import {
  GROUPING_MODES,
  GROUPING_MODE_LABELS,
  type GroupingMode,
  type PoemGroup,
} from './poem-groups.ts'

export interface PoemIndexProps {
  readonly groups: readonly PoemGroup[]
  readonly mode: GroupingMode
  readonly onModeChange: (mode: GroupingMode) => void
  readonly announcement: string
  readonly totalCount: number
  readonly headingId: string
  /**
   * Slot beside the count, for the ebook launcher.
   *
   * A node rather than a flag, for the same reason `PageShell` takes its theme
   * toggle as one: the launcher is a container with a hook in it, and this
   * component is a presenter that owns nothing.
   */
  readonly action?: ReactNode
}

const SECTION_EYEBROW = 'Poemas'
const SECTION_TITLE = 'O que vejo da janela'
const MODE_GROUP_LABEL = 'Ver por'
const MODE_GROUP_LABEL_ID = 'rotulo-modo'

/**
 * Presenter for the poem index. Owns no state.
 *
 * Every one of the 23 poem links is present in the DOM in every mode — regrouping
 * only reorders them. That is what makes this control SEO-neutral: the crawlable
 * link graph is identical whichever mode is active, and the default mode is what
 * gets prerendered.
 */
export function PoemIndex({
  groups,
  mode,
  onModeChange,
  announcement,
  totalCount,
  headingId,
  action,
}: PoemIndexProps): ReactElement {
  return (
    <div className="poem-index">
      <div className="poem-index__header">
        <SectionHeading
          level={2}
          eyebrow={SECTION_EYEBROW}
          title={SECTION_TITLE}
          id={headingId}
        />
        {/* Rendered unconditionally — an absent slot contributes nothing, and a
            branch here would be a branch with no behaviour behind it. */}
        <div className="poem-index__aside">
          <p className="poem-index__count">{`${String(totalCount)} poemas`}</p>
          {action}
        </div>
      </div>

      <div className="poem-index__modes">
        <span className="poem-index__modes-label" id={MODE_GROUP_LABEL_ID}>
          {MODE_GROUP_LABEL}
        </span>
        <div className="poem-index__modes-group" role="group" aria-labelledby={MODE_GROUP_LABEL_ID}>
          {GROUPING_MODES.map((candidate) => (
            <PillButton
              key={candidate}
              variant="toggle"
              pressed={candidate === mode}
              onClick={() => {
                onModeChange(candidate)
              }}
            >
              {GROUPING_MODE_LABELS[candidate]}
            </PillButton>
          ))}
        </div>
      </div>

      {/* Empty on the server; filled only after the reader switches mode. The
          design announced nothing at all, leaving the change silent. */}
      <LiveRegion message={announcement} className="poem-index__announcement" />

      <div className="poem-index__groups">
        {groups.map((group) => (
          <section className="poem-group" key={group.id} aria-labelledby={`grupo-${group.id}`}>
            <SectionHeading
              level={3}
              title={group.label}
              note={group.note}
              id={`grupo-${group.id}`}
            />

            {group.advisory === undefined ? null : (
              <p className="poem-group__advisory">{group.advisory}</p>
            )}

            <ul className="poem-group__list">
              {group.poems.map((poem) => (
                <li className="poem-group__item" key={poem.slug}>
                  <PoemCard view={poemCardView(poem, mode)} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}
